/**
 * audio-gate.js — play() an <audio> element after an await, without losing the
 * user gesture.
 *
 * THE PROBLEM. `el.play()` is only permitted while the click's user activation
 * is alive. Safari drops activation as soon as the handler's task ends, so
 * `fetch(url).then(() => el.play())` is refused with NotAllowedError on any link
 * slow enough that the response outlives the window. Chromium desktop keeps a
 * *document-sticky* activation and plays anyway — so the bug is invisible in
 * local dev and CI — but Chromium on Android uses the strict per-element gate
 * and fails exactly like Safari. Diagnosed 2026-07-08 on /english-level-test,
 * where 31 of ~483 listening sessions had lost their audio for a month.
 *
 * THE MECHANISM. A media element that has played once *inside* a gesture keeps
 * that permission for its lifetime (WebKit:
 * removeBehaviorRestrictionsAfterFirstUserGesture; Blink:
 * AutoplayPolicy::TryUnlockingUserGesture). So arm() plays one second of silence
 * on the element at the first eligible gesture, and from then on the tool may
 * play() whenever its bytes arrive.
 *
 * WHAT WAS MEASURED, not assumed (chromium --autoplay-policy=user-gesture-required,
 * a gate calibrated to reject a play() with no activation; see
 * tests/playwright/audio-gate.mjs):
 *   · the unlock is strictly PER-ELEMENT — a sibling element created later is
 *     still refused. Reusing one element without unlocking it does NOT help.
 *   · it survives a 20 s idle, pause(), src swaps, load(), and src removal.
 *   · a keydown unlocks just as well as a click (keyboard-only users).
 *   · prefetch + a synchronous in-handler play() needs no unlock at all — it is
 *     the stronger path, so prefer it when the URL can be known in advance.
 * And on real WebKit, in production: ios-audio-unmute's identical silent-WAV
 * gesture-play reports 24 764 successful unlocks per 5 days on iPhones — but
 * also 90 post-unlock NotAllowedError rejections, i.e. the permission CAN be
 * taken back (a call, Siri, another tab stealing the audio session). Hence the
 * gesture listeners stay attached for the page's lifetime and a lost unlock
 * re-arms itself on the next gesture, rather than being treated as permanent.
 *
 * USAGE
 *     const el = window.__audioGate.element();     // shared, already armed
 *     // …later, after any number of awaits:
 *     window.__audioGate.play(el, objectUrl).catch(showError);
 *
 *     window.__audioGate.arm(myOwnAudioEl);        // arm an element you own
 *     window.__audioGate.unlockNow(el);            // inside a gesture handler
 *
 * Arm elements that have no src yet — arming a loaded element cannot unlock it
 * without clobbering the media, so it is left alone until it has played.
 *
 * NOT for Web Audio (AudioContext.resume()) and not for the iOS Ring/Silent
 * switch — that is public/js/ios-audio-unmute.js, a different problem.
 */
(function () {
    'use strict';

    // WebKit treats only these as audio-eligible; pointerdown / touchstart ride
    // alongside but do not grant playback (see ios-audio-unmute.js).
    var GESTURES = ['click', 'keydown', 'touchend'];

    var armed = [];
    var unlocked = (typeof WeakSet !== 'undefined') ? new WeakSet() : null;
    var silentUrl = null;
    var attached = false;
    var sharedEl = null;
    var reportedUnlock = false;

    function ae(type, payload) {
        try { if (window.__ae) window.__ae(type, payload || {}); } catch (e) {}
    }

    // 1 s of PCM silence. Built at runtime so no base64 literal rides in the
    // source. Marked non-output: the global createObjectURL tracker in
    // layouts/app.blade.php would otherwise report it as a tool_complete.
    function silentWavUrl() {
        if (silentUrl) return silentUrl;
        var rate = 8000, bytes = rate * 2;
        var buf = new ArrayBuffer(44 + bytes), dv = new DataView(buf);
        var put = function (off, s) { for (var i = 0; i < s.length; i++) dv.setUint8(off + i, s.charCodeAt(i)); };
        put(0, 'RIFF'); dv.setUint32(4, 36 + bytes, true); put(8, 'WAVE');
        put(12, 'fmt '); dv.setUint32(16, 16, true); dv.setUint16(20, 1, true); dv.setUint16(22, 1, true);
        dv.setUint32(24, rate, true); dv.setUint32(28, rate * 2, true); dv.setUint16(32, 2, true); dv.setUint16(34, 16, true);
        put(36, 'data'); dv.setUint32(40, bytes, true);
        var blob = new Blob([buf], { type: 'audio/wav' });
        try { if (window.__skipBlobAutoTrack) window.__skipBlobAutoTrack(blob); } catch (e) {}
        silentUrl = URL.createObjectURL(blob);
        return silentUrl;
    }

    function isUnlocked(el) { return !!(unlocked && el && unlocked.has(el)); }
    function markUnlocked(el) { if (unlocked && el) unlocked.add(el); }
    function markLocked(el) { if (unlocked && el) unlocked.delete(el); }

    // MUST run synchronously inside a user-gesture handler.
    function unlockNow(el) {
        if (!el || isUnlocked(el)) return;
        // Already playing ⇒ the engine already permitted it; never touch its src.
        if (!el.paused) { markUnlocked(el); return; }
        // A loaded-but-paused element cannot be unlocked without destroying the
        // media it holds. Leave it armed; it unlocks the moment it plays.
        if (el.src && el.src !== silentUrl) return;
        try {
            el.src = silentWavUrl();
            markUnlocked(el);
            var p = el.play();
            if (p && p.catch) p.catch(function (err) {
                // Not every gesture is audio-eligible (a keydown on a modifier,
                // a synthetic click). Stay locked; the next one will do it.
                if (err && err.name === 'NotAllowedError') markLocked(el);
            });
            if (!reportedUnlock) {
                reportedUnlock = true;
                ae('feature_use', { f: 'audio_gate_unlock' });
            }
        } catch (e) { markLocked(el); }
    }

    function onGesture() {
        for (var i = 0; i < armed.length; i++) unlockNow(armed[i]);
    }

    // Listeners stay for the page's lifetime: real iOS revokes the permission
    // when something steals the audio session, and only a fresh gesture restores
    // it. Detaching after the first success would strand those users.
    function attach() {
        if (attached) return;
        attached = true;
        for (var i = 0; i < GESTURES.length; i++) document.addEventListener(GESTURES[i], onGesture, true);
    }

    function arm(el) {
        if (!el) return el;
        if (armed.indexOf(el) < 0) armed.push(el);
        attach();
        return el;
    }

    function element() {
        if (!sharedEl) {
            sharedEl = new Audio();
            sharedEl.preload = 'auto';
            try { sharedEl.playsInline = true; sharedEl.setAttribute('playsinline', ''); } catch (e) {}
            arm(sharedEl);
        }
        return sharedEl;
    }

    // Point `el` at `url` and play. Safe to call any number of awaits after the
    // gesture, provided the element was armed (or had already played) before.
    //
    // opts.owned — the caller has its own answer to a refusal (another source,
    // the device voice) and says it itself. Without it the play() observer in
    // timbrica-core.js tells the person «this file cannot be played» — wrong
    // when the next line plays the same thing another way (/alphabet: a clip
    // that did not load falls back to speech synthesis).
    function play(el, url, opts) {
        if (!el) return Promise.reject(new Error('audio_gate_no_element'));
        try {
            if (url && el.src !== url) el.src = url;
            else if (el.ended || el.currentTime) el.currentTime = 0;
        } catch (e) { /* setting currentTime before metadata can throw */ }
        var p;
        try { p = el.play(); } catch (e) { return Promise.reject(e); }
        if (opts && opts.owned && p && typeof p === 'object') { try { p.__tmbPlayOwned = true; } catch (e) {} }
        if (!p || !p.then) { markUnlocked(el); return Promise.resolve(); }
        return p.then(function () { markUnlocked(el); }, function (err) {
            if (err && err.name === 'NotAllowedError') {
                // The permission was never granted, or was taken back. Re-arm so
                // the user's next click restores it, and tell the caller.
                markLocked(el);
                arm(el);
                ae('feature_use', { f: 'audio_gate_relocked', reason: err.name });
            }
            throw err;
        });
    }

    window.__audioGate = {
        arm: arm,
        element: element,
        play: play,
        unlockNow: unlockNow,
        unlocked: isUnlocked,
        // Is `el` playing the unlock silence right now? The dead-click detector
        // (silent-click-detector.js) must not take it for a button's answer:
        // it plays on the first gesture of the page, whatever was pressed.
        isSilence: function (el) {
            try { return !!(el && silentUrl && (el.currentSrc || el.src) === silentUrl); } catch (e) { return false; }
        },
    };
})();
